/** @jsxImportSource @emotion/react */
import { memo, useCallback, useContext, useMemo } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import isMobile from 'ismobilejs';
import { SideBarExpandedContext } from '@trinity/chat-components/components/BrandInsights/context';
import { MessageRegenerateResponseFooter } from '@trinity/chat-components/components/presentation/Message.RegenerateResponse.Footer';
import { ChatResponseType, EngagementType, ChatAttachmentType } from '@trinity/chat';
import type { Chat } from '@trinity/chat';
import { RegenerateMessageContext } from './context';
import { useIsFullPageChat } from './hooks/useIsFullPageChat';
import { ChatMessageAttachments } from './Message.Attachments';
import { SystemAuthor } from './Message.Author';
import { MessageResponse } from './Message.Response';
import { extendedMessageWidth, messageWidth, fullChatExtendedMessageWidth } from './Message.styles';
import { SystemUserMessageFooter, SystemUserMessageFooterContainer } from './Message.System.Footer';
import { useStyles } from './Message.System.styles';
import { ChatMessageVariantSelector } from './Message.VariantSelector';
import { getEngagementType } from './utils';
import { GroupFeaturesContext } from '@trinity/chat-components/containers/Chat';

type Props = {
  className?: string;
  item: Chat.Query;
  hasRegenerateFooter?: boolean;
  isStreaming?: boolean;
};

type BaseProps = {
  children: React.ReactNode;
  className?: string;
  extendedWidth?: boolean;
};

export const BaseSystemUserMessage = memo(({ children, className, extendedWidth }: BaseProps) => {
  const device = isMobile();
  const styles = useStyles();

  const maxWidth = useMemo(() => (extendedWidth ? extendedMessageWidth : messageWidth), [extendedWidth]);

  return (
    <div css={styles.root} className={className}>
      {!device.phone && (
        <div css={styles.authorWrap}>
          <SystemAuthor css={styles.author} />
        </div>
      )}
      <div css={css({ maxWidth })}>
        <div css={css([styles.body, css({ maxWidth })])}>{children}</div>
      </div>
    </div>
  );
});

export const SystemUserMessage = ({
  className,
  item,
  hasRegenerateFooter = false,
  isStreaming = false,
}: Props) => {
  const device = isMobile();
  const styles = useStyles();

  const [expanded] = useContext(SideBarExpandedContext);

  const numCitations = useMemo(() => {
    if (!item.citations) return 0;
    return item.citations.length;
  }, [item.citations]);

  const hasCitations = numCitations > 0;
  const isFullPageChat = useIsFullPageChat();

  const hasHcpSearchAttachment = useMemo(() => {
    return item.attachments.some((attachment) => attachment.type === ChatAttachmentType.HCPSearch);
  }, [item.attachments]);

  const groupFeatures = useContext(GroupFeaturesContext);
  const getEngagementLabels = useCallback((): React.ReactNode => {
    let reactive = false;
    let proactive = false;
    const labels: React.ReactNode[] = [];

    if (numCitations === 0 || !groupFeatures.engagementResponseLabels) {
      return labels;
    }

    for (const citation of item.citations) {
      if (proactive && reactive) break;
      const engagementTypes = getEngagementType(citation);
      if (engagementTypes.includes(EngagementType.Proactive)) {
        proactive = true;
      }
      if (engagementTypes.includes(EngagementType.Reactive)) {
        reactive = true;
      }
    }
    if (proactive) {
      labels.push(
        <span key="proactive" css={styles.engagementLabel}>
          Proactive Engagement
        </span>
      );
    }
    if (reactive) {
      labels.push(
        <span key="reactive" css={styles.engagementLabel}>
          Reactive Engagement
        </span>
      );
    }
    return labels;
  }, [groupFeatures.engagementResponseLabels, item.citations, numCitations, styles.engagementLabel]);

  const bodyWidth = useMemo(() => {
    if (isFullPageChat) return fullChatExtendedMessageWidth;

    if (
      item.response?.type === ChatResponseType.Chart ||
      item.response?.type === ChatResponseType.BIChartQuantAnalyses
    )
      return extendedMessageWidth;

    if (typeof item.response?.value !== 'string') {
      return extendedMessageWidth;
    }

    const isLongResponse = typeof item.response?.value === 'string' && item.response?.value.length > 250;
    const hasFollowups = item.suggestedFollowups.length > 0;
    if ((isLongResponse || hasFollowups) && !expanded) return extendedMessageWidth;

    if (hasCitations) return messageWidth;

    return undefined;
  }, [isFullPageChat, item.response, item.suggestedFollowups.length, expanded, hasCitations]);

  return (
    <div css={styles.root} className={className}>
      {!device.phone && (
        <div css={styles.authorWrap}>
          <SystemAuthor css={styles.author} />
          <ChatMessageVariantSelector item={item} />
        </div>
      )}
      <div
        css={css({
          maxWidth: item.response?.type === ChatResponseType.Chart ? '100%' : bodyWidth,
          width: '100%',
        })}
      >
        <div
          css={css([
            styles.body,
            css({
              maxWidth: item.response?.type === ChatResponseType.Chart ? '100%' : bodyWidth,
              width: '100%',
            }),
          ])}
        >
          {getEngagementLabels()}
          <MessageResponse
            queryIdentifier={item.identifier}
            item={item.response}
            state={item.state}
            status={item.status}
          />
          {!isStreaming && !!item.attachments.length && <StyledAttachments attachments={item.attachments} />}
          {!isStreaming && !hasHcpSearchAttachment && (
            <SystemUserMessageFooterContainer>
              <SystemUserMessageFooter item={item} numCitations={numCitations} />
            </SystemUserMessageFooterContainer>
          )}
        </div>
        {!isStreaming && hasRegenerateFooter && <RegenerateResponse item={item} />}
      </div>
    </div>
  );
};

export const StreamingSystemUserMessage = (props: Omit<Props, 'hasRegenerateFooter' | 'isStreaming'>) => (
  <SystemUserMessage {...props} isStreaming />
);

const StyledAttachments = styled(ChatMessageAttachments)({
  marginTop: 15,
});

type RegenerateResponseProps = {
  item: Chat.Query;
};

const RegenerateResponse = ({ item }: RegenerateResponseProps) => {
  const { onRegenerate } = useContext(RegenerateMessageContext);

  const handleClick = useCallback(() => {
    onRegenerate(item);
  }, [item, onRegenerate]);

  return <MessageRegenerateResponseFooter css={css({ marginTop: 10 })} onClick={handleClick} />;
};
